import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { supabase, isSupabaseConfigured } from '../supabaseClient';
import TextField from '../components/TextField';
import Button from '../components/Button';

function Contact() {
	const [form, setForm] = useState({
		full_name: '',
		email: '',
		phone: '',
		company_name: '',
		service: '',
		message: '',
		agree_to_sms: false,
		agree_to_tcpa: false
	});
	const [submitting, setSubmitting] = useState(false);
	const [status, setStatus] = useState(null);
	const [websiteTrap, setWebsiteTrap] = useState('');

	const onChange = (e) => {
		const { name, value, type, checked } = e.target;
		setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSubmitting(true);
		setStatus(null);
		try {
			// Honeypot trap: if filled, treat as success but do not submit
			if (websiteTrap) {
				setStatus({ ok: true, msg: 'Thank you! We will be in touch shortly.' });
				setSubmitting(false);
				setForm({
					full_name: '', email: '', phone: '', company_name: '', service: '', message: '',
					agree_to_sms: false, agree_to_tcpa: false
				});
				return;
			}
			// Insert lead
			const { data: lead, error: leadError } = await supabase
				.from('apex_specialty_leads')
				.insert([{
					full_name: form.full_name,
					email: form.email,
					phone: form.phone,
					company_name: form.company_name,
					financing_amount: null,
					equipment_type: form.service || null,
					message: form.message || null,
					agree_to_sms: form.agree_to_sms,
					agree_to_tcpa: form.agree_to_tcpa,
					source: 'apex_specialty'
				}])
				.select()
				.single();
			if (leadError) throw leadError;

			// Insert SMS consent audit if consented
			if (form.agree_to_sms) {
				const consentText =
					'I agree to receive text messages from APEX SPECIALTY CONTRACTING LLC including roofing estimates, appointment reminders, project status updates, and promotional offers. ' +
					'Msg & data rates may apply. Message frequency varies. Reply STOP to cancel, HELP for help. ' +
					'Consent not required to receive services.';
				await supabase.from('sms_consents').insert([{
					lead_id: lead?.id ?? null,
					phone: form.phone,
					email: form.email || null,
					name: form.full_name || null,
					consent_text: consentText,
					consent_method: 'web_form_checkbox',
					referral_url: window.location.href,
					user_agent: navigator.userAgent,
					ip_address: null, // backend or edge function can enrich IP if needed
					brand: 'APEX SPECIALTY CONTRACTING LLC',
					website: 'appexconnecthub.com'
				}]);
			}

			setStatus({ ok: true, msg: 'Thank you! We will be in touch shortly.' });
			setForm({
				full_name: '', email: '', phone: '', company_name: '', service: '', message: '',
				agree_to_sms: false, agree_to_tcpa: false
			});
		} catch (err) {
			console.error(err);
			setStatus({ ok: false, msg: 'Something went wrong. Please try again.' });
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<>
			<Helmet>
				<title>Contact Us | Apex Specialty Contracting LLC – Free Roof Estimate</title>
				<meta name="description" content="Contact Apex Specialty Contracting LLC for a free roof inspection and estimate in the Tampa Bay area. Call (916) 316-0696 or fill out our online form." />
			</Helmet>
			<section className="section">
				<div className="container">
					<div className="two-col" style={{ gap: 32, alignItems: 'flex-start' }}>

						{/* Left: Contact Info */}
						<div>
							<h2>Get a Free Estimate</h2>
							<p className="helper">Ready to fix or replace your roof? Reach out and we'll schedule a free, no-obligation inspection at a time that works for you.</p>
							<div className="card" style={{ padding: 18, marginTop: 16 }}>
								<strong>APEX SPECIALTY CONTRACTING LLC</strong>
								<p className="helper" style={{ marginTop: 6 }}>183 Highland Rd<br />Tarpon Springs, FL 34689</p>
								<p className="helper"><strong>Phone:</strong> <a href="tel:9163160696">(916) 316-0696</a></p>
								<p className="helper"><strong>Email:</strong> <a href="mailto:info@appexconnecthub.com">info@appexconnecthub.com</a></p>
								<p className="helper"><strong>Support:</strong> <a href="mailto:support@appexconnecthub.com">support@appexconnecthub.com</a></p>
								<p className="helper"><strong>Hours:</strong> Mon–Fri 7AM–6PM · Sat 8AM–2PM</p>
							</div>
							{!isSupabaseConfigured && (
								<p className="helper" style={{ color: 'crimson', marginTop: 10 }}>
									Note: Form submissions are temporarily offline. Please call or email us directly.
								</p>
							)}
						</div>

						{/* Right: Request Form */}
						<div>
							<h3>Request Form</h3>
							<form className="form form-grid" style={{ marginTop: 12 }} onSubmit={handleSubmit} noValidate>
								{/* Honeypot field */}
								<input
									type="text"
									name="website"
									value={websiteTrap}
									onChange={(e) => setWebsiteTrap(e.target.value)}
									style={{ display: 'none' }}
									tabIndex={-1}
									autoComplete="off"
									aria-hidden="true"
								/>
								<TextField id="full_name" label="Full Name *" name="full_name" required value={form.full_name} onChange={onChange} />
								<TextField id="email" label="Email *" type="email" name="email" required value={form.email} onChange={onChange} placeholder="you@example.com" />
								<TextField id="phone" label="Mobile Phone *" name="phone" required value={form.phone} onChange={onChange} placeholder="(555) 555-5555" helper="Provide a mobile number for SMS communications (optional but recommended)." pattern="^[0-9+()\\-\\s]{7,}$" />
								<TextField id="company_name" label="Property Address" name="company_name" value={form.company_name} onChange={onChange} placeholder="123 Main St, Tampa, FL" />
								<div className="field">
									<label htmlFor="service">Service Needed</label>
									<select id="service" name="service" value={form.service} onChange={onChange}>
										<option value="">Select a service</option>
										<option value="free_inspection">Free Roof Inspection</option>
										<option value="roof_replacement">Roof Replacement</option>
										<option value="storm_damage_repair">Storm / Hail Damage Repair</option>
										<option value="insurance_claim">Insurance Claim Assistance</option>
										<option value="gutter_installation">Gutter Installation or Repair</option>
										<option value="commercial_roofing">Commercial Roofing</option>
										<option value="skylight_ventilation">Skylight / Ventilation</option>
										<option value="other">Other / Not Sure</option>
									</select>
								</div>
								<div className="field" style={{ gridColumn: '1 / -1' }}>
									<label htmlFor="message">Additional Details</label>
									<textarea id="message" name="message" rows={4} value={form.message} onChange={onChange} />
								</div>
								<div className="consentBox" style={{ gridColumn: '1 / -1' }}>
									<p><strong>Communication Preferences</strong></p>
									<div className="consent-grid">
										<div className="consent-row">
											<input
												type="checkbox"
												id="agree_to_sms"
												name="agree_to_sms"
												checked={form.agree_to_sms}
												onChange={onChange}
												style={{ marginTop: 4 }}
											/>
											<label htmlFor="agree_to_sms" className="helper">
												Yes, I agree to receive text messages from APEX SPECIALTY CONTRACTING LLC including roofing estimates, appointment reminders, project status updates, and promotional offers. Msg &amp; data rates may apply. Frequency varies. Reply <strong>STOP</strong> to cancel, <strong>HELP</strong> for help. Consent not required to receive services.
											</label>
										</div>
										<div className="consent-row">
											<input
												type="checkbox"
												id="agree_to_tcpa"
												name="agree_to_tcpa"
												required
												checked={form.agree_to_tcpa}
												onChange={onChange}
												style={{ marginTop: 4 }}
											/>
											<label htmlFor="agree_to_tcpa" className="helper">
												I agree that APEX SPECIALTY CONTRACTING LLC may contact me by phone, email, or SMS at the information provided to discuss roofing services, schedule appointments, and send updates about my project. <strong>(Required)</strong>
											</label>
										</div>
									</div>
									<p className="helper" style={{ marginTop: 8 }}>
										By submitting this form you agree to our <a href="/privacy">Privacy Policy</a> and <a href="/terms">Terms of Use</a>. View our <a href="/sms-policy">SMS Policy</a>.
									</p>
								</div>
								<div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-start' }}>
									<Button type="submit" disabled={submitting || !isSupabaseConfigured}>
										{submitting ? 'Submitting…' : 'Request Free Estimate'}
									</Button>
								</div>
								{status && (
									<p className="helper" style={{ color: status.ok ? 'green' : 'crimson' }}>{status.msg}</p>
								)}
							</form>
						</div>

					</div>
				</div>
			</section>
		</>
	);
}

export default Contact;
